import React from "react";
import { flushSync } from "react-dom";
import { Rnd } from "react-rnd";
import { createDeferredResizeObserver, getHitAreaLinkInputSeed, isDefaultHitAreaUrlValue } from "./utils";

export default function HitAreaCanvasSection({
  isHitAreaLimitReached,
  hitAreaLimitTooltip,
  imageSource,
  isPreviewImageError,
  previewAreaRef,
  previewStageRef,
  canvasAreaRef,
  previewImageRef,
  previewStageDimensions = { width: 0, height: 0 },
  imageWidth,
  imageHeight,
  canDrawHitAreas,
  isPreviewStageReady = false,
  isDrawToolActive,
  handleStageMouseDown,
  hitAreas,
  selectedHitAreaId,
  getResolvedHitAreaRectAtTime,
  currentTimelineSecond,
  imageToDisplayRect,
  boundaryWarningIds,
  isHitAreaActiveAtCurrentTime,
  selectHitAreaFromCanvas,
  createDimensionText,
  deleteHitAreaById,
  selectedHitArea,
  selectedDisplayRect,
  isSelectedHitAreaActive,
  isSelectedHitAreaLocked,
  isSelectedHitAreaVisible,
  selectedImageRectAtCurrentTime,
  selectedHitAreaKeyframe,
  pendingKeyframeTime = null,
  minRectSize,
  isHitAreaTransformingRef,
  applyAlignmentSnapToDisplayRect,
  constrainDisplayRectToNonOverlap,
  updateHitAreaFromDisplayRect,
  setSmartFeedback,
  buildSmartFeedbackFromDisplayRect,
  queueHistoryCommit,
  activeLinkPanelHitArea,
  hitAreaLinkPanelStyle,
  isHitAreaLinkEditing,
  hitAreaLinkError,
  hitAreaLinkInput,
  selectedHitAreaTargetLink,
  beginLinkEditForHitArea,
  setHitAreaLinkInput,
  setHitAreaLinkError,
  setIsHitAreaLinkEditing,
  commitSelectedHitAreaLink,
  onHitAreaLinkPanelHeightChange,
  smartFeedback,
  draftRect,
  displayToImageRect,
  setIsPreviewImageLoaded,
  setIsPreviewImageError,
  refreshStageSize,
  isTimelinePlaying = false,
  isTimelineMarkerScrubbing = false,
  getHitAreaModalPreviewElementId,
}) {
  const isTimelinePreviewActive = isTimelinePlaying || isTimelineMarkerScrubbing;
  const hitAreaLinkInputRef = React.useRef(null);
  const hitAreaLinkEditorRef = React.useRef(null);
  const hitAreaRndRef = React.useRef(null);
  const [isHitAreaTransforming, setIsHitAreaTransforming] = React.useState(false);
  const [liveTransformDisplayRect, setLiveTransformDisplayRect] = React.useState(null);
  const transformStartDisplayRectRef = React.useRef(null);
  const moveConstraintFallbackRef = React.useRef(null);
  const transformModeRef = React.useRef(null);
  const moveDragSizeRef = React.useRef(null);
  const hitAreaPreviewOverlayRef = React.useRef(null);
  const [limitTooltipPosition, setLimitTooltipPosition] = React.useState(null);

  const syncLimitTooltipFromPointer = React.useCallback(
    (clientX, clientY) => {
      if (!isHitAreaLimitReached) {
        setLimitTooltipPosition(null);
        return;
      }

      const overlay = hitAreaPreviewOverlayRef.current;
      if (!overlay) return;

      const rect = overlay.getBoundingClientRect();
      const isInsideOverlay =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom;

      if (isInsideOverlay) {
        setLimitTooltipPosition({ x: clientX, y: clientY });
      } else {
        setLimitTooltipPosition(null);
      }
    },
    [isHitAreaLimitReached]
  );

  React.useEffect(() => {
    if (!isHitAreaLimitReached) {
      setLimitTooltipPosition(null);
      return undefined;
    }

    const handlePointerMove = (event) => {
      syncLimitTooltipFromPointer(event.clientX, event.clientY);
    };

    const handlePointerUp = (event) => {
      syncLimitTooltipFromPointer(event.clientX, event.clientY);
    };

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
    };
  }, [isHitAreaLimitReached, syncLimitTooltipFromPointer]);

  const handleOverlayMouseDown = React.useCallback(
    (event) => {
      if (isTimelinePreviewActive) return;

      if (isHitAreaLimitReached) {
        event.preventDefault();
        event.stopPropagation();
        syncLimitTooltipFromPointer(event.clientX, event.clientY);
        handleStageMouseDown(event);
        return;
      }

      handleStageMouseDown(event);
    },
    [
      handleStageMouseDown,
      isHitAreaLimitReached,
      isTimelinePreviewActive,
      syncLimitTooltipFromPointer,
    ]
  );

  const activeSelectedDisplayRect =
    liveTransformDisplayRect || selectedDisplayRect;

  const applyConstrainedRndRect = React.useCallback(
    (resolvedRect, rndPosition = null, { positionOnly = false } = {}) => {
      const rnd = hitAreaRndRef.current;
      if (!rnd || !resolvedRect) return;

      if (!positionOnly) {
        rnd.updateSize({
          width: resolvedRect.width,
          height: resolvedRect.height,
        });
      }

      if (rndPosition) {
        const deltaX = resolvedRect.x - rndPosition.x;
        const deltaY = resolvedRect.y - rndPosition.y;
        if (Math.abs(deltaX) >= 0.5 || Math.abs(deltaY) >= 0.5) {
          const currentPosition = rnd.getDraggablePosition();
          rnd.updatePosition({
            x: currentPosition.x + deltaX,
            y: currentPosition.y + deltaY,
          });
          return;
        }
      }

      rnd.updatePosition({
        x: resolvedRect.x,
        y: resolvedRect.y,
      });
    },
    []
  );

  React.useLayoutEffect(() => {
    if (!isHitAreaTransforming || !activeSelectedDisplayRect) return;
    if (transformModeRef.current !== "move") return;
    applyConstrainedRndRect(activeSelectedDisplayRect, null, {
      positionOnly: true,
    });
  }, [activeSelectedDisplayRect, applyConstrainedRndRect, isHitAreaTransforming]);

  const hasSavedHitAreaLink = React.useMemo(() => {
    if (!activeLinkPanelHitArea) return false;
    return !isDefaultHitAreaUrlValue(activeLinkPanelHitArea.targetLink);
  }, [activeLinkPanelHitArea]);

  const showLinkDisplayRow = hasSavedHitAreaLink && !isHitAreaLinkEditing;

  const displayedHitAreaLinkValue = React.useMemo(() => {
    if (showLinkDisplayRow) {
      const savedLink = String(activeLinkPanelHitArea?.targetLink ?? "");
      return isDefaultHitAreaUrlValue(savedLink) ? "" : savedLink;
    }
    return hitAreaLinkInput;
  }, [
    activeLinkPanelHitArea,
    hitAreaLinkInput,
    showLinkDisplayRow,
  ]);

  React.useEffect(() => {
    if (!activeLinkPanelHitArea || activeLinkPanelHitArea.lockBoolean) return;
    if (!showLinkDisplayRow && !isHitAreaLinkEditing) {
      setIsHitAreaLinkEditing(true);
    }
  }, [
    activeLinkPanelHitArea,
    isHitAreaLinkEditing,
    setIsHitAreaLinkEditing,
    showLinkDisplayRow,
  ]);

  React.useLayoutEffect(() => {
    if (showLinkDisplayRow) return;
    if (!hitAreaLinkInputRef.current) return;
    hitAreaLinkInputRef.current.focus();
  }, [activeLinkPanelHitArea?.id, showLinkDisplayRow]);

  React.useLayoutEffect(() => {
    const panelElement = hitAreaLinkEditorRef.current;
    if (!panelElement || !activeLinkPanelHitArea || isHitAreaTransforming) {
      onHitAreaLinkPanelHeightChange?.(0);
      return undefined;
    }

    const reportHeight = () => {
      onHitAreaLinkPanelHeightChange?.(panelElement.offsetHeight);
    };

    reportHeight();
    const resizeObserver = createDeferredResizeObserver(reportHeight);
    if (!resizeObserver) {
      return undefined;
    }
    resizeObserver.observe(panelElement);

    return () => {
      resizeObserver.cancelPendingFrame();
      resizeObserver.disconnect();
    };
  }, [
    activeLinkPanelHitArea,
    displayedHitAreaLinkValue,
    hitAreaLinkError,
    hitAreaLinkPanelStyle?.width,
    isHitAreaLinkEditing,
    isHitAreaTransforming,
    onHitAreaLinkPanelHeightChange,
    showLinkDisplayRow,
  ]);

  const isLinkPanelOpen =
    !!activeLinkPanelHitArea && !!hitAreaLinkPanelStyle && !isHitAreaTransforming;

  const isCanvasDrawEnabled = isDrawToolActive && canDrawHitAreas;
  const canRenderHitAreaOverlays = isPreviewStageReady && !isPreviewImageError;
  const canTransformSelectedHitArea = isDrawToolActive && !isSelectedHitAreaLocked;
  const overlayModeClass = isDrawToolActive
    ? canDrawHitAreas
      ? "isToolActive"
      : "isDrawDisabled"
    : "isToolInactive";

  const lockedMoveDisplaySize =
    isHitAreaTransforming &&
    transformModeRef.current === "move" &&
    moveDragSizeRef.current
      ? moveDragSizeRef.current
      : null;
  const rndDisplaySize = lockedMoveDisplaySize || activeSelectedDisplayRect;

  const resolveSelectedMoveRect = React.useCallback(
    (clientX, clientY, startPointer, startRect, fallbackDisplayRect) => {
      const lockedSize = moveDragSizeRef.current || startRect;
      const candidateRect = {
        x: startRect.x + (clientX - startPointer.x),
        y: startRect.y + (clientY - startPointer.y),
        width: lockedSize.width,
        height: lockedSize.height,
      };
      const snappedRect = applyAlignmentSnapToDisplayRect(
        selectedHitArea.id,
        candidateRect,
        "move"
      );
      const resolvedRect =
        constrainDisplayRectToNonOverlap(
          {
            ...snappedRect,
            width: lockedSize.width,
            height: lockedSize.height,
          },
          hitAreas,
          selectedHitArea.id,
          {
            ...fallbackDisplayRect,
            width: lockedSize.width,
            height: lockedSize.height,
          },
          currentTimelineSecond,
          {
            transformMode: "move",
            anchorDisplayRect: startRect,
          }
        ) || {
          ...fallbackDisplayRect,
          width: lockedSize.width,
          height: lockedSize.height,
        };
      return {
        ...resolvedRect,
        width: lockedSize.width,
        height: lockedSize.height,
      };
    },
    [
      applyAlignmentSnapToDisplayRect,
      constrainDisplayRectToNonOverlap,
      currentTimelineSecond,
      hitAreas,
      selectedHitArea,
    ]
  );

  const handleSelectedHitAreaMouseDown = React.useCallback(
    (event) => {
      if (!canTransformSelectedHitArea || isSelectedHitAreaLocked || !selectedHitArea) return;
      if (event.button !== 0) return;
      if (event.target.closest(".react-resizable-handle")) return;
      if (pendingKeyframeTime !== null) return;

      event.preventDefault();
      event.stopPropagation();
      selectHitAreaFromCanvas(selectedHitArea.id);

      const startPointer = { x: event.clientX, y: event.clientY };
      const startRect = {
        ...(activeSelectedDisplayRect || selectedDisplayRect),
      };
      moveDragSizeRef.current = {
        width: startRect.width,
        height: startRect.height,
      };
      transformStartDisplayRectRef.current = startRect;
      moveConstraintFallbackRef.current = startRect;
      transformModeRef.current = "move";
      setLiveTransformDisplayRect(null);
      isHitAreaTransformingRef.current = true;
      setIsHitAreaTransforming(true);

      const handleMouseMove = (moveEvent) => {
        const fallbackDisplayRect = moveConstraintFallbackRef.current || startRect;
        const resolvedRect = resolveSelectedMoveRect(
          moveEvent.clientX,
          moveEvent.clientY,
          startPointer,
          startRect,
          fallbackDisplayRect
        );
        moveConstraintFallbackRef.current = resolvedRect;
        flushSync(() => {
          setLiveTransformDisplayRect(resolvedRect);
        });
        applyConstrainedRndRect(resolvedRect, null, { positionOnly: true });
        updateHitAreaFromDisplayRect(selectedHitArea.id, resolvedRect, {
          mode: "move",
          timeInSeconds: currentTimelineSecond,
          keyframeId: selectedHitAreaKeyframe?.id,
        });
        setSmartFeedback(
          buildSmartFeedbackFromDisplayRect(selectedHitArea.id, resolvedRect)
        );
      };

      const handleMouseUp = (upEvent) => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        isHitAreaTransformingRef.current = false;
        setIsHitAreaTransforming(false);
        const fallbackDisplayRect = moveConstraintFallbackRef.current || startRect;
        const resolvedRect = resolveSelectedMoveRect(
          upEvent.clientX,
          upEvent.clientY,
          startPointer,
          startRect,
          fallbackDisplayRect
        );
        applyConstrainedRndRect(resolvedRect, null, { positionOnly: true });
        updateHitAreaFromDisplayRect(selectedHitArea.id, resolvedRect, {
          mode: "move",
          timeInSeconds: currentTimelineSecond,
          keyframeId: selectedHitAreaKeyframe?.id,
        });
        setLiveTransformDisplayRect(null);
        transformStartDisplayRectRef.current = null;
        moveConstraintFallbackRef.current = null;
        moveDragSizeRef.current = null;
        transformModeRef.current = null;
        setSmartFeedback(null);
        queueHistoryCommit();
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [
      activeSelectedDisplayRect,
      applyConstrainedRndRect,
      buildSmartFeedbackFromDisplayRect,
      canTransformSelectedHitArea,
      currentTimelineSecond,
      isSelectedHitAreaLocked,
      pendingKeyframeTime,
      queueHistoryCommit,
      resolveSelectedMoveRect,
      selectHitAreaFromCanvas,
      selectedDisplayRect,
      selectedHitArea,
      selectedHitAreaKeyframe?.id,
      setSmartFeedback,
      updateHitAreaFromDisplayRect,
    ]
  );

  // Canvas rendering: preview surface, overlays, and active editors.
  return (
    <div
      className={`hitAreaModalCanvasArea ${isLinkPanelOpen ? "hasLinkPanelOpen" : ""}`}
      ref={canvasAreaRef}
    >
      <div
        className={`hitAreaPreviewArea ${isLinkPanelOpen ? "hasLinkPanelOpen" : ""} ${smartFeedback ? "hasSmartFeedback" : ""}`}
        ref={previewAreaRef}
      >
        {imageSource && !isPreviewImageError ? (
          <div
            className={`hitAreaPreviewStage ${isCanvasDrawEnabled ? "isToolActive" : ""} ${isLinkPanelOpen ? "hasLinkPanelOpen" : ""} ${smartFeedback ? "hasSmartFeedback" : ""}`}
            ref={previewStageRef}
            style={
              previewStageDimensions.width > 0 && previewStageDimensions.height > 0
                ? {
                    width: `${previewStageDimensions.width}px`,
                    height: `${previewStageDimensions.height}px`,
                  }
                : undefined
            }
            onDragStart={(event) => event.preventDefault()}
          >
            <img
              ref={previewImageRef}
              src={imageSource}
              alt="Selected layer preview"
              className="hitAreaPreviewImage"
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              onLoad={() => {
                setIsPreviewImageLoaded(true);
                setIsPreviewImageError(false);
                refreshStageSize();
              }}
              onError={() => {
                setIsPreviewImageLoaded(false);
                setIsPreviewImageError(true);
              }}
            />
            <div
              ref={hitAreaPreviewOverlayRef}
              className={`hitAreaPreviewOverlay ${overlayModeClass} ${isTimelinePreviewActive ? "isTimelinePlaying" : ""}`}
              aria-label={isHitAreaLimitReached ? hitAreaLimitTooltip : undefined}
              onMouseEnter={(event) =>
                syncLimitTooltipFromPointer(event.clientX, event.clientY)
              }
              onMouseDown={isTimelinePreviewActive ? undefined : handleOverlayMouseDown}
              onDragStart={(event) => event.preventDefault()}
            >
              {limitTooltipPosition && isHitAreaLimitReached ? (
                <div
                  className="hitAreaLimitDragTooltip"
                  style={{
                    left: limitTooltipPosition.x + 12,
                    top: limitTooltipPosition.y + 16,
                  }}
                >
                  {hitAreaLimitTooltip}
                </div>
              ) : null}
              {isTimelinePreviewActive && canRenderHitAreaOverlays
                ? hitAreas
                    .filter((item) => !item.hideBoolean)
                    .map((item) => {
                      const isBoundaryWarning = boundaryWarningIds.has(item.id);
                      const isActiveAtCurrentTime = isHitAreaActiveAtCurrentTime(item);
                      const isSelected = selectedHitAreaId === item.id;
                      return (
                        <div
                          key={item.id}
                          id={getHitAreaModalPreviewElementId(item.id)}
                          data-hit-area-interactive="true"
                          className={`hitAreaPreviewBox hitAreaPreviewBoxPlayback ${isBoundaryWarning ? "isBoundaryWarning" : ""} ${isActiveAtCurrentTime ? "" : "isInactive"} ${isSelected ? "isSelected" : ""} ${item.lockBoolean ? "isLocked" : ""}`}
                          style={{ position: "absolute" }}
                        />
                      );
                    })
                : null}
              {!isTimelinePreviewActive &&
              canRenderHitAreaOverlays &&
              hitAreas
                .filter((item) => item.id !== selectedHitAreaId && !item.hideBoolean)
                .map((item) => {
                  const resolvedRect =
                    getResolvedHitAreaRectAtTime(item, currentTimelineSecond) || item;
                  const previewRect = imageToDisplayRect(resolvedRect);
                  const isBoundaryWarning = boundaryWarningIds.has(item.id);
                  const isActiveAtCurrentTime = isHitAreaActiveAtCurrentTime(item);
                  const isLocked = !!item.lockBoolean;
                  return (
                    <div
                      key={item.id}
                      data-hit-area-interactive="true"
                      className={`hitAreaPreviewBox ${isBoundaryWarning ? "isBoundaryWarning" : ""} ${isActiveAtCurrentTime ? "" : "isInactive"} ${isLocked ? "isLocked" : ""}`}
                      style={{
                        left: previewRect.x,
                        top: previewRect.y,
                        width: previewRect.width,
                        height: previewRect.height,
                      }}
                      role={isActiveAtCurrentTime ? "button" : undefined}
                      tabIndex={isActiveAtCurrentTime ? 0 : -1}
                      aria-disabled={!isActiveAtCurrentTime}
                      onMouseDown={(event) => {
                        if (!isActiveAtCurrentTime) return;
                        event.stopPropagation();
                        // mousedown fires before blur on the URL input.
                        // Commit any in-progress link edit before switching
                        // hit areas so the typed URL isn't discarded.
                        if (isHitAreaLinkEditing) {
                          commitSelectedHitAreaLink(hitAreaLinkInput, {});
                          setIsHitAreaLinkEditing(false);
                        }
                        if (!selectHitAreaFromCanvas(item.id)) return;
                        if (pendingKeyframeTime !== null) return;
                      }}
                      onKeyDown={(event) => {
                        if (!isActiveAtCurrentTime) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          if (!selectHitAreaFromCanvas(item.id)) return;
                          if (pendingKeyframeTime !== null) return;
                        }
                      }}
                    >
                      <span className="hitAreaDimensionTag">
                        {createDimensionText(resolvedRect.width, resolvedRect.height)}
                      </span>
                      <button
                        type="button"
                        className="hitAreaDeleteBadge hitAreaDeleteBadgePreview"
                        aria-label={`Delete ${item.name || "hit area"}`}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        disabled={isLocked}
                        title={isLocked ? "Unlock hit area to delete" : ""}
                        onClick={(event) => {
                          if (isLocked) return;
                          deleteHitAreaById(item.id, event);
                        }}
                      >
                        {"\u2715"}
                      </button>
                    </div>
                  );
                })}

              {/* Selected hit area: full-overlay wrapper for Rnd bounds; custom drag for move. */}
              {!isTimelinePreviewActive &&
              canRenderHitAreaOverlays &&
              selectedHitArea &&
              selectedDisplayRect &&
              isSelectedHitAreaActive && (
                <div className="hitAreaSelectedRndWrapper">
                  <Rnd
                    ref={hitAreaRndRef}
                    className="hitAreaSelectedRnd selectedComp"
                    data-hit-area-interactive="true"
                    size={{
                      width: rndDisplaySize.width,
                      height: rndDisplaySize.height,
                    }}
                    position={{
                      x: activeSelectedDisplayRect.x,
                      y: activeSelectedDisplayRect.y,
                    }}
                    bounds="parent"
                    minWidth={minRectSize}
                    minHeight={minRectSize}
                    disableDragging
                    enableResizing={
                      canTransformSelectedHitArea
                        ? {
                            top: true,
                            topRight: true,
                            right: true,
                            bottomRight: true,
                            bottom: true,
                            bottomLeft: true,
                            left: true,
                            topLeft: true,
                          }
                        : false
                    }
                    resizeHandleClasses={{
                      topRight: "topAnchorRight",
                      topLeft: "topAnchorLeft",
                      bottomLeft: "bottomAnchorLeft",
                      bottomRight: "bottomAnchorRight",
                      top: "horizontalAnchorCenter",
                      bottom: "horizontalAnchorCenter",
                      left: "verticallAnchorCenter",
                      right: "verticallAnchorCenter",
                    }}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                      selectHitAreaFromCanvas(selectedHitArea.id);
                      if (pendingKeyframeTime !== null) return;
                    }}
                    onResizeStart={() => {
                      if (!canTransformSelectedHitArea) return;
                      transformModeRef.current = "resize";
                      transformStartDisplayRectRef.current =
                        activeSelectedDisplayRect || selectedDisplayRect;
                      setLiveTransformDisplayRect(null);
                      isHitAreaTransformingRef.current = true;
                      setIsHitAreaTransforming(true);
                    }}
                    onResize={(event, direction, ref, delta, position) => {
                      if (!canTransformSelectedHitArea) return;
                      const startRect =
                        transformStartDisplayRectRef.current || selectedDisplayRect;
                      const candidateRect = {
                        x: position.x,
                        y: position.y,
                        width: ref.offsetWidth,
                        height: ref.offsetHeight,
                      };
                      const snappedRect = applyAlignmentSnapToDisplayRect(
                        selectedHitArea.id,
                        candidateRect,
                        "resize",
                        String(direction || "")
                      );
                      const resolvedRect =
                        constrainDisplayRectToNonOverlap(
                          snappedRect,
                          hitAreas,
                          selectedHitArea.id,
                          startRect,
                          currentTimelineSecond,
                          {
                            resizeDirection: String(direction || ""),
                            anchorDisplayRect: startRect,
                          }
                        ) || startRect;
                      flushSync(() => {
                        setLiveTransformDisplayRect(resolvedRect);
                      });
                      applyConstrainedRndRect(resolvedRect, position);
                      updateHitAreaFromDisplayRect(selectedHitArea.id, resolvedRect, {
                        mode: "resize",
                        timeInSeconds: currentTimelineSecond,
                        keyframeId: selectedHitAreaKeyframe?.id,
                      });
                      setSmartFeedback(
                        buildSmartFeedbackFromDisplayRect(selectedHitArea.id, resolvedRect)
                      );
                    }}
                    onResizeStop={(event, direction, ref, delta, position) => {
                      if (!canTransformSelectedHitArea) return;
                      isHitAreaTransformingRef.current = false;
                      setIsHitAreaTransforming(false);
                      const startRect =
                        transformStartDisplayRectRef.current || selectedDisplayRect;
                      const candidateRect = {
                        x: position.x,
                        y: position.y,
                        width: ref.offsetWidth,
                        height: ref.offsetHeight,
                      };
                      const snappedRect = applyAlignmentSnapToDisplayRect(
                        selectedHitArea.id,
                        candidateRect,
                        "resize",
                        String(direction || "")
                      );
                      const resolvedRect =
                        constrainDisplayRectToNonOverlap(
                          snappedRect,
                          hitAreas,
                          selectedHitArea.id,
                          startRect,
                          currentTimelineSecond,
                          {
                            resizeDirection: String(direction || ""),
                            anchorDisplayRect: startRect,
                          }
                        ) || startRect;
                      applyConstrainedRndRect(resolvedRect, position);
                      updateHitAreaFromDisplayRect(selectedHitArea.id, resolvedRect, {
                        mode: "resize",
                        timeInSeconds: currentTimelineSecond,
                        keyframeId: selectedHitAreaKeyframe?.id,
                      });
                      setLiveTransformDisplayRect(null);
                      transformStartDisplayRectRef.current = null;
                      transformModeRef.current = null;
                      setSmartFeedback(null);
                      queueHistoryCommit();
                    }}
                  >
                    <div
                      className={`hitAreaSelectedBox ${boundaryWarningIds.has(selectedHitArea.id) ? "isBoundaryWarning" : ""} ${isDrawToolActive ? "isToolActive" : "isToolInactive"} ${isSelectedHitAreaLocked ? "isLocked" : ""}`}
                      onMouseDown={handleSelectedHitAreaMouseDown}
                    >
                    <span className="hitAreaDimensionTag hitAreaDimensionTagSelected">
                      {createDimensionText(
                        selectedImageRectAtCurrentTime?.width ??
                          selectedHitAreaKeyframe?.width ??
                          selectedHitArea.width,
                        selectedImageRectAtCurrentTime?.height ??
                          selectedHitAreaKeyframe?.height ??
                          selectedHitArea.height
                      )}
                    </span>
                    <button
                      type="button"
                      className="hitAreaDeleteBadge"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      disabled={isSelectedHitAreaLocked}
                      title={isSelectedHitAreaLocked ? "Unlock hit area to delete" : ""}
                      onClick={(event) => {
                        if (isSelectedHitAreaLocked || !selectedHitArea) return;
                        deleteHitAreaById(selectedHitArea.id, event);
                      }}
                      aria-label="Delete selected hit area"
                    >
                      {"\u2715"}
                    </button>
                  </div>
                </Rnd>
                </div>
              )}

              {canRenderHitAreaOverlays &&
                selectedHitArea &&
                selectedDisplayRect &&
                !isSelectedHitAreaActive &&
                isSelectedHitAreaVisible && (
                  <div
                    data-hit-area-interactive="true"
                    className={`hitAreaPreviewBox isInactive ${boundaryWarningIds.has(selectedHitArea.id) ? "isBoundaryWarning" : ""}`}
                    style={{
                      left: selectedDisplayRect.x,
                      top: selectedDisplayRect.y,
                      width: selectedDisplayRect.width,
                      height: selectedDisplayRect.height,
                    }}
                  >
                    <span className="hitAreaDimensionTag">
                      {createDimensionText(
                        selectedImageRectAtCurrentTime?.width ??
                          selectedHitAreaKeyframe?.width ??
                          selectedHitArea.width,
                        selectedImageRectAtCurrentTime?.height ??
                          selectedHitAreaKeyframe?.height ??
                          selectedHitArea.height
                      )}
                    </span>
                  </div>
                )}

              {/* Smart feedback layer: alignment guides, gap measurements, and warnings. */}
              {smartFeedback && (
                <div className="hitAreaSmartFeedbackLayer">
                  {smartFeedback.snapGuides.map((guide) =>
                    guide.orientation === "vertical" ? (
                      <div
                        key={guide.id}
                        className="hitAreaSnapGuide vertical"
                        style={{
                          left: guide.x,
                          top: guide.y1,
                          height: Math.max(1, guide.y2 - guide.y1),
                        }}
                      />
                    ) : (
                      <div
                        key={guide.id}
                        className="hitAreaSnapGuide horizontal"
                        style={{
                          top: guide.y,
                          left: guide.x1,
                          width: Math.max(1, guide.x2 - guide.x1),
                        }}
                      />
                    )
                  )}

                  {smartFeedback.gapLines.map((line) => {
                    const left = Math.min(line.x1, line.x2);
                    const top = Math.min(line.y1, line.y2);
                    const width = Math.abs(line.x2 - line.x1);
                    const height = Math.abs(line.y2 - line.y1);
                    const labelLeft =
                      line.orientation === "horizontal" ? left + width / 2 : left + 14;
                    const labelTop =
                      line.orientation === "horizontal" ? top - 10 : top + height / 2;

                    return (
                      <div key={line.id}>
                        <div
                          className={`hitAreaGapLine ${line.orientation} ${line.isWarning ? "isWarning" : ""}`}
                          style={
                            line.orientation === "horizontal"
                              ? {
                                  left,
                                  top,
                                  width: Math.max(1, width),
                                }
                              : {
                                  left,
                                  top,
                                  height: Math.max(1, height),
                                }
                          }
                        />
                        <span
                          className={`hitAreaGapLabel ${line.isWarning ? "isWarning" : ""}`}
                          style={{
                            left: labelLeft,
                            top: labelTop,
                          }}
                        >
                          {line.label}
                        </span>
                      </div>
                    );
                  })}

                  {smartFeedback.boundaryBadges.map((badge) => (
                    <span
                      key={badge.key}
                      className={`hitAreaBoundaryBadge ${badge.key} ${badge.isWarning ? "isWarning" : ""}`}
                      style={{
                        left: badge.x,
                        top: badge.y,
                      }}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>
              )}

              {canRenderHitAreaOverlays && draftRect && (
                <div
                  className="hitAreaDraftBox"
                  style={{
                    left: draftRect.x,
                    top: draftRect.y,
                    width: draftRect.width,
                    height: draftRect.height,
                  }}
                >
                  <span className="hitAreaDimensionTag hitAreaDimensionTagDraft">
                    {createDimensionText(
                      displayToImageRect(draftRect).width,
                      displayToImageRect(draftRect).height
                    )}
                  </span>
                </div>
              )}

            </div>
          </div>
        ) : (
          <div className="hitAreaPreviewFallback">No image selected</div>
        )}
      </div>

      {isLinkPanelOpen && (
        <div
          ref={hitAreaLinkEditorRef}
          className="hitAreaLinkEditor"
          data-hit-area-interactive="true"
          data-hit-area-link-panel="true"
          style={hitAreaLinkPanelStyle}
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
        >
          {showLinkDisplayRow ? (
            <div className="hitAreaLinkDisplayRow">
              <div
                className="hitAreaLinkText"
                title={displayedHitAreaLinkValue}
              >
                {displayedHitAreaLinkValue}
              </div>
              <button
                type="button"
                className={`hitAreaLinkEditButton ${activeLinkPanelHitArea?.lockBoolean ? "isDisabled" : ""}`}
                disabled={!!activeLinkPanelHitArea?.lockBoolean}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (activeLinkPanelHitArea?.lockBoolean) return;
                  beginLinkEditForHitArea(activeLinkPanelHitArea);
                }}
              >
                EDIT
              </button>
            </div>
          ) : (
            <div
              className={`hitAreaLinkEditRow isEditing ${activeLinkPanelHitArea?.lockBoolean ? "isLocked" : ""}`}
              title={
                activeLinkPanelHitArea?.lockBoolean
                  ? "Unlock hit area to edit link"
                  : ""
              }
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
            >
              <input
                ref={hitAreaLinkInputRef}
                type="text"
                className={`hitAreaLinkInput ${hitAreaLinkError ? "hasError" : ""}`}
                placeholder="Type or paste Hit Area URL"
                readOnly={!!activeLinkPanelHitArea?.lockBoolean}
                value={displayedHitAreaLinkValue}
                onChange={(event) => {
                  if (!isHitAreaLinkEditing) {
                    setIsHitAreaLinkEditing(true);
                  }
                  setHitAreaLinkInput(event.target.value);
                  if (hitAreaLinkError) {
                    setHitAreaLinkError("");
                  }
                }}
                onBlur={() => {
                  if (showLinkDisplayRow) return;
                  commitSelectedHitAreaLink(hitAreaLinkInput, {
                    closeEditorOnSuccess: true,
                  });
                }}
                onKeyDown={(event) => {
                  if (showLinkDisplayRow) return;
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitSelectedHitAreaLink(hitAreaLinkInput, {
                      closeEditorOnSuccess: true,
                    });
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setHitAreaLinkInput(
                      getHitAreaLinkInputSeed(selectedHitAreaTargetLink)
                    );
                    setHitAreaLinkError("");
                    setIsHitAreaLinkEditing(false);
                  }
                }}
              />
            </div>
          )}
          {hitAreaLinkError && (
            <span className="hitAreaLinkError">{hitAreaLinkError}</span>
          )}
        </div>
      )}
    </div>
  );
}
